import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import useSWR from "swr";
import api from "@/lib/api";
import { formatIDR, formatDate, todayISO, daysBetween } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CircleDollarSign, ChevronLeft } from "lucide-react";

const fetcher = (url) => api.get(url).then((r) => r.data);
const STATUS_TONE = {
  unpaid: "bg-warning/15 text-warning",
  partial: "bg-info/15 text-info",
  paid: "bg-success/15 text-success",
};
const STATUS_LABEL = { unpaid: "Belum Bayar", partial: "Cicilan", paid: "Lunas" };

export default function BillDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: bill } = useSWR(`/bills/${id}`, fetcher);
  const { data: payments = [] } = useSWR(bill ? `/payments?bill_id=${bill.id}` : null, fetcher);
  const { data: unit } = useSWR(bill ? `/units/${bill.unit_id}` : null, fetcher);
  const { data: tenant } = useSWR(bill ? `/tenants/${bill.tenant_id}` : null, fetcher);

  if (!bill) return <div className="text-sm text-muted-foreground">Memuat...</div>;
  const outstanding = Number(bill.amount) + Number(bill.late_fee || 0) - Number(bill.paid_amount || 0);
  const overdue = bill.status !== "paid" && bill.due_date < todayISO();

  return (
    <div className="space-y-6" data-testid="bill-detail-page">
      <div>
        <Button variant="ghost" size="sm" className="gap-1 -ml-2" onClick={() => navigate("/bills")}>
          <ChevronLeft className="h-4 w-4" /> Kembali
        </Button>
      </div>

      <header className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Detail Tagihan</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">
            {formatIDR(bill.amount)}
          </h1>
          <div className="flex items-center gap-2 mt-2">
            <Badge className="rounded-md font-mono">{unit?.code}</Badge>
            <Badge className={`rounded-full text-[10px] uppercase tracking-wider ${STATUS_TONE[bill.status]}`}>
              {STATUS_LABEL[bill.status]}
            </Badge>
            {overdue && (
              <Badge variant="destructive" className="rounded-full text-[10px] uppercase tracking-wider">
                Lewat {daysBetween(bill.due_date, todayISO())} hari
              </Badge>
            )}
          </div>
        </div>
        {bill.status !== "paid" && (
          <div className="sm:ml-auto">
            <Button className="gap-2" onClick={() => navigate(`/bills?open=${bill.id}`)}>
              <CircleDollarSign className="h-4 w-4" /> Bayar dari list
            </Button>
          </div>
        )}
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-6 space-y-3 text-sm">
            <Row label="Penyewa" value={tenant?.name} />
            <Row label="Unit" value={`${unit?.code || ""} — ${unit?.name || ""}`} />
            <Row label="Periode" value={`${formatDate(bill.period_start)} – ${formatDate(bill.period_end)}`} />
            <Row label="Jatuh Tempo" value={formatDate(bill.due_date)} />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Nilai Tagihan</span><span className="tabular-nums">{formatIDR(bill.amount)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Denda Keterlambatan</span><span className="tabular-nums text-destructive">{formatIDR(bill.late_fee)}</span></div>
            <div className="flex justify-between border-t pt-2"><span>Total</span><span className="tabular-nums font-medium">{formatIDR(bill.amount + bill.late_fee)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Sudah Dibayar</span><span className="tabular-nums">{formatIDR(bill.paid_amount)}</span></div>
            <div className="flex justify-between border-t pt-2 font-display text-lg"><span>Sisa</span><span className="tabular-nums">{formatIDR(outstanding)}</span></div>
          </CardContent>
        </Card>
      </div>

      <div>
        <h2 className="font-display text-xl font-medium tracking-tight mb-3">Riwayat Pembayaran</h2>
        {payments.length === 0 ? (
          <Card><CardContent className="p-6 text-sm text-muted-foreground">Belum ada pembayaran.</CardContent></Card>
        ) : (
          <div className="space-y-2">
            {payments.map((p) => (
              <Card key={p.id} data-testid={`payment-row-${p.id}`}>
                <CardContent className="p-4 flex items-center justify-between gap-4">
                  <div>
                    <div className="font-mono text-xs text-muted-foreground">{p.receipt_number}</div>
                    <div className="font-medium mt-0.5">{formatIDR(p.amount)}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatDate(p.paid_at)} · {p.method === "cash" ? "Cash" : "Transfer"}
                      {p.note ? ` · ${p.note}` : ""}
                    </div>
                  </div>
                  <Link
                    to={`/receipt/${p.id}`}
                    className="text-sm font-medium text-primary hover:underline"
                    data-testid={`view-receipt-${p.id}`}
                  >
                    Lihat kwitansi →
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value || "—"}</span>
    </div>
  );
}
